import React from 'react';
import { useI18n } from '../../i18n';
import {
  PERMISSION_CATALOGUE,
  toggleRoleSelection,
  type CompanyRole,
} from '../../services/roleService';
import { formatHourlyRate, roleDescription, roleDisplayName, roleKindSummary } from './staffPresentation';

interface Props {
  /** The roles the person choosing is allowed to give. */
  roles: CompanyRole[];
  selectedIds: string[];
  onChange: (roleIds: string[]) => void;
  disabled?: boolean;
  /** Roles shown ticked and fixed, each with the reason it cannot be taken away. */
  locked?: Record<string, string>;
  /** Distinguishes the inputs when two pickers could be on the page. */
  idPrefix: string;
}

/**
 * Chooses the roles one person holds: at most one guard role, and any number
 * of management roles. Picking a second guard role replaces the first, which
 * is the same thing the database does.
 */
export default function RolePicker({ roles, selectedIds, onChange, disabled = false, locked = {}, idPrefix }: Props) {
  const { t } = useI18n();
  const guardRoles = roles.filter((role) => role.kind === 'guard');
  const managementRoles = roles.filter((role) => role.kind === 'management');

  const detailFor = (role: CompanyRole): string => {
    if (role.kind === 'guard') {
      return role.defaultHourlyRate !== null
        ? t('Default pay {rate} per hour', { rate: formatHourlyRate(role.defaultHourlyRate) })
        : roleDescription(role);
    }
    if (role.systemKey === 'system_admin') return t('Every area, including billing, pay rates and roles');
    if (role.permissions.length === 0) return t('No workspace areas yet');
    return PERMISSION_CATALOGUE
      .filter((permission) => role.permissions.includes(permission.key))
      .map((permission) => t(permission.label))
      .join(', ');
  };

  const renderGroup = (title: string, summary: string, group: CompanyRole[]) => (
    <fieldset className="role-picker__group" disabled={disabled}>
      <legend className="role-picker__legend">{title}</legend>
      <p className="role-picker__summary">{summary}</p>
      <div className="role-picker__options">
        {group.map((role) => {
          const inputId = `${idPrefix}-role-${role.id}`;
          const lockReason = locked[role.id];
          return (
            <label key={role.id} className="role-picker__option" htmlFor={inputId}>
              <input
                id={inputId}
                type="checkbox"
                checked={selectedIds.includes(role.id)}
                disabled={Boolean(lockReason)}
                onChange={() => onChange(toggleRoleSelection(selectedIds, role, roles))}
              />
              <span className="role-picker__text">
                <span className="role-picker__name">
                  {role.colour && (
                    <span className="role-picker__swatch" style={{ background: role.colour }} aria-hidden="true" />
                  )}
                  {roleDisplayName(role)}
                </span>
                <span className="role-picker__detail">{lockReason ?? detailFor(role)}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );

  return (
    <div className="role-picker">
      {guardRoles.length > 0 && renderGroup(
        t('Guard role'),
        `${roleKindSummary('guard')} ${t('One at a time: choosing another replaces it.')}`,
        guardRoles,
      )}
      {managementRoles.length > 0 && renderGroup(
        t('Management roles'),
        roleKindSummary('management'),
        managementRoles,
      )}
    </div>
  );
}
